import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import RedeemNotificationBanner from './components/RedeemNotificationBanner';
import { SolanaLiveProvider } from './context/SolanaLiveContext';
import Home from './pages/Home';
import Portfolio from './pages/Portfolio';
import Redeem from './pages/Redeem';
import HowItWorks from './pages/HowItWorks';
import Subscriptions from './pages/Subscriptions';
import { createSolanaConnection, getPhantomProvider, SOLANA_RPC_ENDPOINT } from './helper/solana';
import { useGameEndNotification } from './hooks/useGameEndNotification';

function App() {
  const [walletPublicKey, setWalletPublicKey] = useState('');
  const [walletConnected, setWalletConnected] = useState(false);
  const [phantomInstalled, setPhantomInstalled] = useState(false);
  const [connectionState, setConnectionState] = useState({
    connection: null,
    endpoint: SOLANA_RPC_ENDPOINT,
    isReady: false,
    error: '',
  });

  useEffect(() => {
    try {
      const connection = createSolanaConnection();

      setConnectionState({
        connection,
        endpoint: SOLANA_RPC_ENDPOINT,
        isReady: true,
        error: '',
      });
    } catch (error) {
      setConnectionState({
        connection: null,
        endpoint: SOLANA_RPC_ENDPOINT,
        isReady: false,
        error: error.message || 'Unable to create Solana connection.',
      });
    }
  }, []);

  useEffect(() => {
    const provider = getPhantomProvider();

    setPhantomInstalled(Boolean(provider));

    if (!provider) {
      setWalletPublicKey('');
      setWalletConnected(false);
      return undefined;
    }

    const syncWalletState = (publicKey) => {
      if (!publicKey) {
        setWalletPublicKey('');
        setWalletConnected(false);
        return;
      }

      setWalletPublicKey(publicKey.toString());
      setWalletConnected(true);
    };

    const handleConnect = (publicKey) => {
      syncWalletState(publicKey || provider.publicKey);
    };

    const handleDisconnect = () => {
      setWalletPublicKey('');
      setWalletConnected(false);
    };

    const handleAccountChanged = (publicKey) => {
      syncWalletState(publicKey);
    };

    if (provider.isConnected && provider.publicKey) {
      syncWalletState(provider.publicKey);
    }

    provider.on?.('connect', handleConnect);
    provider.on?.('disconnect', handleDisconnect);
    provider.on?.('accountChanged', handleAccountChanged);

    return () => {
      provider.off?.('connect', handleConnect);
      provider.off?.('disconnect', handleDisconnect);
      provider.off?.('accountChanged', handleAccountChanged);
      provider.removeListener?.('connect', handleConnect);
      provider.removeListener?.('disconnect', handleDisconnect);
      provider.removeListener?.('accountChanged', handleAccountChanged);
    };
  }, []);

  const connectWallet = async () => {
    const provider = getPhantomProvider();

    setPhantomInstalled(Boolean(provider));

    if (!provider) {
      return;
    }

    try {
      const response = await provider.connect();

      setWalletPublicKey(response.publicKey.toString());
      setWalletConnected(true);
    } catch (error) {
      setWalletPublicKey('');
      setWalletConnected(false);
    }
  };

  const disconnectWallet = async () => {
    const provider = getPhantomProvider();

    if (!provider) {
      setWalletPublicKey('');
      setWalletConnected(false);
      return;
    }

    try {
      await provider.disconnect();
    } finally {
      setWalletPublicKey('');
      setWalletConnected(false);
    }
  };

  return (
    <SolanaLiveProvider>
      <BrowserRouter>
        <AppContent
          connectionState={connectionState}
          connectWallet={connectWallet}
          disconnectWallet={disconnectWallet}
          phantomInstalled={phantomInstalled}
          walletConnected={walletConnected}
          walletPublicKey={walletPublicKey}
        />
      </BrowserRouter>
    </SolanaLiveProvider>
  );
}

function AppContent({
  connectionState,
  connectWallet,
  disconnectWallet,
  phantomInstalled,
  walletConnected,
  walletPublicKey,
}) {
  const location = useLocation();
  const { notification, dismissNotification } = useGameEndNotification(walletPublicKey);

  // Dismiss notification when route changes
  useEffect(() => {
    dismissNotification();
  }, [location.pathname, dismissNotification]);

  return (
    <div className="app-shell">
      <div className="app-shell__glow app-shell__glow--left" />
      <div className="app-shell__glow app-shell__glow--right" />

      <RedeemNotificationBanner
        notification={notification}
        onDismiss={dismissNotification}
      />

      <Navbar
        connectionState={connectionState}
        onConnectWallet={connectWallet}
        onDisconnectWallet={disconnectWallet}
        phantomInstalled={phantomInstalled}
        walletConnected={walletConnected}
        walletPublicKey={walletPublicKey}
      />

      <main className="page-wrap">
        <Routes>
          <Route
            path="/"
            element={
              <Home
                connectionState={connectionState}
                phantomInstalled={phantomInstalled}
                walletConnected={walletConnected}
                walletPublicKey={walletPublicKey}
              />
            }
          />
          <Route
            path="/portfolio"
            element={
              <Portfolio
                walletConnected={walletConnected}
                walletPublicKey={walletPublicKey}
              />
            }
          />
          <Route
            path="/redeem"
            element={
              <Redeem
                walletConnected={walletConnected}
                walletPublicKey={walletPublicKey}
              />
            }
          />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route
            path="/subscriptions"
            element={
              <Subscriptions
                phantomInstalled={phantomInstalled}
                walletConnected={walletConnected}
                walletPublicKey={walletPublicKey}
              />
            }
          />
        </Routes>
      </main>
    </div>
  );
}

export default App;
